import { AfterViewInit, Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, NgForm, Validators } from "@angular/forms";
import { AuthService } from "../../main/auth.service";
import { ArkUserType, SupportTicketDisplayStatusModel, SupportTicketStatusMappingModel } from "../../core/api/models";
import { TicketStatusMappingsApiService } from "../../core/api/services";
import { Utils } from "../../core/utils";
import { AlertService } from "../../shared/services/alert.service";
import { Table } from "primeng/table";

@Component({
  selector: 'app-ticket-status-mappings',
  templateUrl: './ticket-status-mappings.component.html',
  standalone: false
})
export class TicketStatusMappingsComponent implements OnInit, AfterViewInit {
  @ViewChild('itemForm') itemForm!: NgForm;
  @ViewChild('dt1') table?: Table;

  filter: any = {};
  list: any = undefined;
  loadingList: boolean = true;
  loading: boolean = false;
  currentItem?: SupportTicketStatusMappingModel;
  itemFormGroup!: FormGroup;
  displayStatusFormGroup!: FormGroup;
  displayStatuses: string[] = [];
  displayStatusItems: SupportTicketDisplayStatusModel[] = [];
  editingDisplayStatus: boolean = false;
  savingDisplayStatus: boolean = false;
  currentDisplayStatus?: SupportTicketDisplayStatusModel;

  constructor(
    private authService: AuthService,
    private alertService: AlertService,
    private apiService: TicketStatusMappingsApiService,
    private formBuilder: FormBuilder) {
  }

  ngOnInit(): void {
    this.authService.checkIfAutorized([ArkUserType.Support, ArkUserType.Admin]);

    this.itemFormGroup = this.formBuilder.group({
      GitHubStatus: ['', [Validators.required, Validators.maxLength(100)]],
      DisplayStatus: ['', [Validators.required, Validators.maxLength(100)]],
      Archived: [false, []],
    });

    this.displayStatusFormGroup = this.formBuilder.group({
      DisplayStatus: ['', [Validators.required, Validators.maxLength(100)]],
    });

    this.loadOptions();
  }

  loadOptions(selectedDisplayStatus?: string) {
    this.apiService.Options()
      .forEach(options => {
        this.displayStatuses = options.DisplayStatuses || [];
        this.displayStatusItems = options.DisplayStatusItems || [];

        if (selectedDisplayStatus) {
          this.itemFormGroup.patchValue({
            DisplayStatus: selectedDisplayStatus
          });
        }
      }).catch(err => {
        this.alertService.error(Utils.getAjaxError(err));
      });
  }

  ngAfterViewInit(): void {
  }

  loadTable(event: any) {
    this.filter = Utils.normalizePrimeDatatableEvent(event || { filters: {} });

    this.loadingList = true;
    this.apiService.List({ body: this.filter })
      .forEach(result => {
        this.list = result;
        this.loadingList = false;
      }).catch(err => {
        this.alertService.error(Utils.getAjaxError(err));
        this.loadingList = false;
      });
  }

  clear(table: Table) {
    this.table = table;
    table.clear();
  }

  addItem() {
    this.currentItem = {
      ID: 0,
      Archived: false
    } as SupportTicketStatusMappingModel;
    this.load();
  }

  onRowEditInit(item: SupportTicketStatusMappingModel) {
    this.loadingList = true;

    this.apiService.Get({ body: { id: item.ID || 0 } })
      .forEach(data => {
        this.loadingList = false;
        this.currentItem = data;
        this.load();
      }).catch(err => {
        this.loadingList = false;
        this.alertService.error(Utils.getAjaxError(err));
      });
  }

  onRowDeleteInit(item: SupportTicketStatusMappingModel) {
    this.alertService.confirm('Confirm', `Archive ${item.GitHubStatus}?`).subscribe(response => {
      if (!response) {
        return;
      }

      this.apiService.Delete({ body: { id: item.ID || 0 } })
        .forEach(() => {
          this.loadTable(null);
          this.alertService.success('Mapping archived');
        }).catch(err => {
          this.alertService.error(Utils.getAjaxError(err));
        });
    });
  }

  load() {
    this.itemFormGroup.setValue({
      GitHubStatus: this.currentItem?.GitHubStatus || '',
      DisplayStatus: this.currentItem?.DisplayStatus || '',
      Archived: this.currentItem?.Archived || false,
    });
  }

  cancel() {
    this.currentItem = undefined;
    this.cancelDisplayStatusEdit();
  }

  save() {
    if (!this.currentItem || this.itemFormGroup.invalid) {
      return;
    }

    this.loading = true;
    const formValue = this.itemFormGroup.value;
    const model = {
      ...this.currentItem,
      GitHubStatus: formValue.GitHubStatus,
      DisplayStatus: formValue.DisplayStatus,
      Archived: formValue.Archived
    } as SupportTicketStatusMappingModel;

    this.apiService.Save({ body: model })
      .forEach(saved => {
        this.loading = false;
        this.currentItem = saved;
        this.loadTable(null);
        this.alertService.success('Mapping saved');
      }).catch(err => {
        this.loading = false;
        this.alertService.error(Utils.getAjaxError(err));
      });
  }

  addDisplayStatus() {
    this.currentDisplayStatus = {
      ID: 0,
      Archived: false
    } as SupportTicketDisplayStatusModel;

    this.displayStatusFormGroup.setValue({
      DisplayStatus: ''
    });
    this.editingDisplayStatus = true;
  }

  editDisplayStatus() {
    const selectedDisplayStatus = this.itemFormGroup.value.DisplayStatus;
    if (!selectedDisplayStatus) {
      return;
    }

    const matchingItem = this.displayStatusItems.find(item =>
      (item.DisplayStatus || '').toLowerCase() === selectedDisplayStatus.toLowerCase());

    this.currentDisplayStatus = matchingItem || {
      ID: 0,
      DisplayStatus: selectedDisplayStatus,
      Archived: false
    } as SupportTicketDisplayStatusModel;

    this.displayStatusFormGroup.setValue({
      DisplayStatus: selectedDisplayStatus
    });
    this.editingDisplayStatus = true;
  }

  cancelDisplayStatusEdit() {
    this.editingDisplayStatus = false;
    this.currentDisplayStatus = undefined;
    this.displayStatusFormGroup.reset({
      DisplayStatus: ''
    });
  }

  saveDisplayStatus() {
    if (!this.currentDisplayStatus || this.displayStatusFormGroup.invalid) {
      return;
    }

    this.savingDisplayStatus = true;
    const displayStatus = this.displayStatusFormGroup.value.DisplayStatus;
    const model = {
      ...this.currentDisplayStatus,
      DisplayStatus: displayStatus,
      Archived: false
    } as SupportTicketDisplayStatusModel;

    this.apiService.SaveDisplayStatus({ body: model })
      .forEach(saved => {
        this.savingDisplayStatus = false;
        this.cancelDisplayStatusEdit();
        this.loadOptions(saved.DisplayStatus || displayStatus);
        this.loadTable(null);
        this.alertService.success('Display status saved');
      }).catch(err => {
        this.savingDisplayStatus = false;
        this.alertService.error(Utils.getAjaxError(err));
      });
  }
}
